import React, { useEffect, useState, useCallback } from 'react';
import {
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  FormGroup,
  Input
} from 'reactstrap';
import Select from 'react-select';
import { connect } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { debounce } from 'lodash';
import { getChildList, getChildInfo } from '../../api/child';
import { getBatchList, getBatchInfo } from '../../api/batch';

const FilterModal = ({
  isOpen,
  handleClose,
  filters,
  setFilters,
  setFilterCount,
  authUser
}) => {
  const [tempFilters, setTempFilters] = useState(filters);
  const [childOptions, setChildOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  useEffect(() => {
    if (!filters || !authUser) return;
    const handleFetchDefaultOptions = async () => {
      setOptionsLoading(true);
      if (filters?.childrenId) {
        const childResponse = await getChildInfo(filters?.childrenId);
        if (childResponse) {
          const option = {
            value: childResponse._id,
            label: childResponse.name
          };
          handleSetOptions(setChildOptions, [option]);
          setTempFilters((prev) => ({ ...prev, childrenId: option }));
        }
      }
      if (filters.batchId) {
        const batchResponse = await getBatchInfo(filters.batchId);
        if (batchResponse) {
          const option = {
            value: batchResponse._id,
            label: batchResponse.name
          };
          handleSetOptions(setBatchOptions, [option]);
          setTempFilters((prev) => ({ ...prev, batchId: option }));
        }
      }
      setOptionsLoading(false);
    };
    handleFetchDefaultOptions();
  }, [filters, authUser]);

  const searchChild = useCallback(
    debounce((event) => {
      if (event && event !== '') {
        fetchChildOptions(1, 50, event);
      }
    }, 300),
    []
  );
  const searchBatch = useCallback(
    debounce((event) => {
      if (event && event !== '') {
        fetchBatchOptions(1, 50, event);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchChildOptions(1, 10);
    fetchBatchOptions(1, 10);
  }, []);

  const fetchChildOptions = async (page, limit, searchParam) => {
    setOptionsLoading(true);
    const response = await getChildList(
      page,
      limit,
      searchParam && { filter_name: searchParam }
    );
    if (response) {
      handleSetOptions(
        setChildOptions,
        response.data.map((item) => ({
          value: item._id,
          label: item.name
        }))
      );
    }
    setOptionsLoading(false);
  };
  const fetchBatchOptions = async (page, limit, searchParam) => {
    setOptionsLoading(true);
    const response = await getBatchList(
      page,
      limit,
      searchParam && { name: searchParam }
    );
    if (response) {
      handleSetOptions(
        setBatchOptions,
        response.data.map((item) => ({
          value: item._id,
          label: item.name
        }))
      );
    }
    setOptionsLoading(false);
  };

  const handleSetOptions = (setHook, newList) => {
    setHook((prev) => {
      const existingIds = new Set(prev.map((item) => item.value));
      const filteredNewList = newList.filter(
        (item) => !existingIds.has(item.value)
      );
      return [...prev, ...filteredNewList];
    });
  };

  useEffect(() => {
    if (!filters) return;
    setTempFilters({
      start_date: filters.start_date ? filters.start_date : null,
      end_date: filters.end_date ? filters.end_date : null,
      isPaid: filters.isPaid,
      childrenId: filters?.childrenId
        ? childOptions.find((c) => c._id === filters?.childrenId)
        : '',
      batchId: filters?.batchId
        ? batchOptions.find((b) => b._id === filters?.batchId)
        : ''
    });
  }, [filters]);

  const handleApplyFilter = () => {
    const finalFilters = {};
    let count = 0;
    if (tempFilters.isPaid === true || tempFilters.isPaid === false) {
      finalFilters.isPaid = tempFilters?.isPaid;
      count++;
    }
    if (tempFilters?.childrenId) {
      finalFilters.childrenId = tempFilters?.childrenId.value;
      count++;
    }
    if (tempFilters?.batchId) {
      finalFilters.batchId = tempFilters?.batchId.value;
      count++;
    }
    if (tempFilters?.start_date && tempFilters?.end_date) {
      finalFilters.start_date = tempFilters.start_date;
      finalFilters.end_date = tempFilters.end_date;
      count++;
    }
    setFilters(finalFilters);
    setFilterCount(count);
    handleClose();
  };

  const handleResetFilter = () => {
    setFilterCount(0);
    setFilters({
      isPaid: '',
      start_date: '',
      end_date: '',
      childrenId: '',
      batchId: ''
    });
    handleClose();
  };

  function dateRangeHandler(setHook) {
    return function (dates) {
      const [start, end] = dates;
      setHook((prev) => ({
        ...prev,
        start_date: start,
        end_date: end
      }));
    };
  }

  const selectStyles = {
    menu: (base) => ({
      ...base,
      zIndex: 100
    })
  };
  return (
    <React.Fragment>
      <Modal isOpen={isOpen} backdrop="static" size="md">
        <ModalHeader toggle={handleClose}>Filter Queries</ModalHeader>
        <ModalBody>
          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Paid Status</Label>
                <div className="d-flex gap-4 align-items-center mb-2" dir="ltr">
                  <div className="custom-control custom-radio">
                    <Input
                      type="radio"
                      id="paid"
                      name="isPaid"
                      className="custom-control-input"
                      checked={tempFilters.isPaid === true}
                      onChange={() =>
                        setTempFilters((prev) => ({ ...prev, isPaid: true }))
                      }
                    />
                    <Label className="custom-control-label" htmlFor="paid">
                      Paid
                    </Label>
                  </div>
                  <div className="custom-control custom-radio mx-3">
                    <Input
                      type="radio"
                      id="unpaid"
                      name="isPaid"
                      className="custom-control-input"
                      checked={tempFilters.isPaid === false}
                      onChange={() =>
                        setTempFilters((prev) => ({ ...prev, isPaid: false }))
                      }
                    />
                    <Label className="custom-control-label" htmlFor="unpaid">
                      Unpaid
                    </Label>
                  </div>
                </div>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Child</Label>
                <Select
                  options={childOptions}
                  onChange={(selected) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      childrenId: selected
                    }))
                  }
                  value={tempFilters.childrenId}
                  styles={selectStyles}
                  onInputChange={searchChild}
                  placeholder="Select Child"
                  disabled={optionsLoading}
                  isLoading={optionsLoading}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Batch</Label>
                <Select
                  options={batchOptions}
                  onChange={(selected) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      batchId: selected
                    }))
                  }
                  value={tempFilters.batchId}
                  styles={selectStyles}
                  onInputChange={searchBatch}
                  placeholder="Select Batch"
                  disabled={optionsLoading}
                  isLoading={optionsLoading}
                />
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup>
                <Label>Time Range</Label>
                <DatePicker
                  selected={tempFilters.start_date}
                  selectsStart
                  className="form-control"
                  startDate={tempFilters.start_date}
                  endDate={tempFilters.end_date}
                  dateFormat="dd-MM-yyyy"
                  selectsRange={true}
                  onChange={dateRangeHandler(setTempFilters)}
                  isClearable={true}
                  placeholderText="Select time range"
                />
              </FormGroup>
            </Col>

            <Col>
              <ModalFooter>
                <Button color="primary" size="sm" onClick={handleApplyFilter}>
                  Apply Filter
                </Button>
                <Button color="success" size="sm" onClick={handleResetFilter}>
                  Reset
                </Button>
              </ModalFooter>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    authUser: state.Login?.user
  };
};

export default connect(mapStateToProps)(FilterModal);
